// RiskGrid — base SVG grid component with appetite colouring and axis labels.
// Convention: Likelihood on X-axis (left to right), Impact on Y-axis (bottom to top).
// Renders cells, appetite backgrounds, and axis labels. Overlays (journey, heatmap)
// are provided as children.

import type { ReactNode } from 'react'
import type { GridConfig, AppetiteMap } from './types'
import {
  VIEWBOX_W,
  VIEWBOX_H,
  MARGIN,
  cellWidth,
  cellHeight,
  cellOrigin,
  appetiteKey,
} from './risk-grid-utils'

interface RiskGridProps {
  config: GridConfig
  appetite: AppetiteMap
  children?: ReactNode
}

export function RiskGrid({ config, appetite, children }: RiskGridProps) {
  const cols = config.likelihoodLevels.length
  const rows = config.impactLevels.length
  const cw = cellWidth(cols)
  const ch = cellHeight(rows)
  const hasAppetiteData = Object.keys(appetite).length > 0

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      width="100%"
      role="img"
      aria-label="Risk scoring grid showing likelihood against impact"
      className="block"
    >
      {/* Arrow marker definition for journey overlay */}
      <defs>
        <marker
          id="grid-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="hsl(var(--muted-foreground))" />
        </marker>
      </defs>

      {/* Cell rectangles with appetite colouring */}
      {config.likelihoodLevels.map((lLevel) =>
        config.impactLevels.map((iLevel) => {
          const origin = cellOrigin(lLevel.value, iLevel.value, cols, rows)
          const key = appetiteKey(lLevel.value, iLevel.value)
          const inAppetite = hasAppetiteData ? appetite[key] === true : true
          return (
            <rect
              key={`cell-${lLevel.value}-${iLevel.value}`}
              x={origin.x}
              y={origin.y}
              width={cw}
              height={ch}
              fill={inAppetite ? 'hsl(var(--grid-appetite-in))' : 'hsl(var(--grid-appetite-out))'}
              stroke="hsl(var(--border))"
              strokeWidth={1}
            />
          )
        }),
      )}

      {/* Y-axis labels (impact) — vertically centered per row, impact increases upward */}
      {config.impactLevels.map((level) => {
        const origin = cellOrigin(1, level.value, cols, rows)
        return (
          <text
            key={`y-${level.value}`}
            x={MARGIN.left - 6}
            y={origin.y + ch / 2}
            textAnchor="end"
            dominantBaseline="central"
            fontSize={11}
            fill="hsl(var(--muted-foreground))"
          >
            {level.value}
          </text>
        )
      })}

      {/* Y-axis title */}
      <text
        x={12}
        y={MARGIN.top + (VIEWBOX_H - MARGIN.top - MARGIN.bottom) / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-90, 12, ${MARGIN.top + (VIEWBOX_H - MARGIN.top - MARGIN.bottom) / 2})`}
      >
        Impact →
      </text>

      {/* X-axis labels (likelihood) — horizontally centered per column */}
      {config.likelihoodLevels.map((level) => {
        const origin = cellOrigin(level.value, 1, cols, rows)
        return (
          <text
            key={`x-${level.value}`}
            x={origin.x + cw / 2}
            y={MARGIN.top + rows * ch + 18}
            textAnchor="middle"
            fontSize={11}
            fill="hsl(var(--muted-foreground))"
          >
            {level.value}
          </text>
        )
      })}

      {/* X-axis title */}
      <text
        x={MARGIN.left + (VIEWBOX_W - MARGIN.left - MARGIN.right) / 2}
        y={VIEWBOX_H - 8}
        textAnchor="middle"
        fontSize={11}
        fill="hsl(var(--muted-foreground))"
      >
        Likelihood →
      </text>

      {/* Overlay children (journey dots, heatmap dots, etc.) */}
      {children}
    </svg>
  )
}
